import { DocumentServicesObject } from '@wix/document-services-types';
import {
  IntentInstallOptions,
  PlatformApiMethods,
} from '@wix/editor-platform-host-integration-apis';
import { PlatformContext } from '../../../../types/platformApi';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import * as unifiedComponents from '../../unifiedComponents/installation/addApps';
import { IntentInstallStore } from '../IntentInstallStore';
import type { ProgressBarManager } from '../ProgressBarManager';
import { ADD_APP_ERR, BEFORE_INSTALL_APP_HISTORY_LABEL } from '../constants';
import { IntentInstallErrorCode, createIntentInstallError } from '../errors';
import { getCommonAppOptions } from './helpers';
import { InstallationStrategy } from './types';

type BaseInstallationStrategyOptions = {
  shouldUpdateProgressBar?: boolean;
};

export class BaseInstallationStrategy
  implements InstallationStrategy<BaseInstallationStrategyOptions>
{
  constructor(
    private platformApiMethods: PlatformApiMethods,
    private documentServicesAPI: DocumentServicesObject,
    private experimentsAPI: IExperimentsApiAdapter,
    private intentInstallStore: IntentInstallStore,
    private progressBarManager: ProgressBarManager,
  ) {}

  private getErrorHandler(
    context: PlatformContext,
    strategyOptions?: BaseInstallationStrategyOptions,
  ) {
    return async (
      error: Error,
      errorName: string,
      rawAppDefinitionId: string,
    ) => {
      let appDefinitionId = rawAppDefinitionId;

      try {
        appDefinitionId = JSON.parse(rawAppDefinitionId)?.[0];
      } catch {}

      this.intentInstallStore.addAppWithError(appDefinitionId);

      console.error(
        createIntentInstallError(
          IntentInstallErrorCode.UnifiedInstallationError,
          errorName,
        )
          .withParentError(error)
          .withAppDefinitionId(appDefinitionId),
      );

      let errorDesc;
      try {
        errorDesc = error?.message || JSON.stringify(error);
      } catch (e) {
        errorDesc = 'cannot stringify error from Document Management';
      }

      // TODO(intent-install): add logging

      if (errorName === ADD_APP_ERR) {
        await context.platformApiMethods.document.history.performUndoUntilLabel(
          {},
          '',
          { snapshotLabel: BEFORE_INSTALL_APP_HISTORY_LABEL + appDefinitionId },
        );
      }

      if (strategyOptions?.shouldUpdateProgressBar) {
        this.progressBarManager.startFakeProgressBarForNextApp();
      }
    };
  }

  public async install(
    context: PlatformContext,
    apps: string[],
    options: IntentInstallOptions,
    strategyOptions?: BaseInstallationStrategyOptions,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      unifiedComponents
        .installApps(
          context,
          this.documentServicesAPI,
          apps,
          {
            noForcedSaveAfterInstallationCompleted: true, // there is custom save at the end of intent install
            appsOptions: getCommonAppOptions(
              this.intentInstallStore,
              apps,
              options,
              this.experimentsAPI,
            ),
          },
          {
            finishAllCallback: async () => {
              resolve();
            },
            onError: this.getErrorHandler(context, strategyOptions),
          },
        )
        .catch((e) => {
          console.error(e);
          resolve();
        });
    });
  }
}
